import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { useState, useEffect } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useAuth } from '../../contexts/AuthContext';
import { dashboardService } from '../../services/dashboard.service';
import { Users, Table } from 'lucide-react';

interface CustomerMapProps {
  dateRange?: { start: Date; end: Date };
}

export function CustomerMap({ dateRange }: CustomerMapProps) {
  const { orderByTable } = useRestaurant();
  const { user } = useAuth();
  const [data, setData] = useState<Array<{ label: string; value: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [_error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [orderByTable, user?.restaurantId, dateRange]);

  const fetchData = async () => {
    if (!user?.restaurantId) return;

    setLoading(true);
    setError(null);
    try {
      if (orderByTable) {
        // Fetch orders per table
        console.log('Fetching orders per table for restaurant:', user.restaurantId);
        const response = await dashboardService.getOrdersPerTable(user.restaurantId, dateRange);
        console.log('Orders per table response:', response);
        
        if (response.success && response.data) {
          // Transform data for the chart - limit to top 20 tables
          const chartData = response.data.slice(0, 20).map(item => ({
            label: item.table,
            value: item.orders,
          }));
          console.log('Transformed chart data:', chartData);
          setData(chartData);
        } else {
          console.warn('No table data in response:', response);
          setData([]);
        }
      } else {
        // Fetch customer orders distribution
        console.log('Fetching customer orders distribution for restaurant:', user.restaurantId);
        const response = await dashboardService.getCustomerOrdersDistribution(user.restaurantId, dateRange);
        console.log('Customer orders distribution response:', response);
        
        if (response.success && response.data) {
          // Transform data for the chart
          const chartData = response.data.map(item => ({
            label: item.customer.split(' ')[0], // Just first name for space
            value: item.orders,
          }));
          setData(chartData);
        } else {
          console.warn('No customer data in response:', response);
          setData([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch customer map data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const maxValue = Math.max(...data.map(d => d.value), 0);
  const yAxisMax = Math.ceil(maxValue * 1.2 / 10) * 10; // Round up to nearest 10 with 20% padding

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-800 transition-all duration-200 hover:shadow-lg hover:-translate-y-1" style={{ height: '320px' }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {orderByTable ? (
            <Table className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          ) : (
            <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          )}
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {orderByTable ? 'Popular Tables' : 'Top Customers'}
          </h3>
        </div>
        {data.length > 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {orderByTable ? `${data.length} tables` : `${data.length} customers`}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[220px]">
          <div className="animate-pulse flex flex-col items-center gap-2">
            <div className="h-32 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      ) : data.length === 0 ? (
        <div className="flex items-center justify-center h-[220px]">
          <div className="text-center">
            <div className="text-gray-400 dark:text-gray-500 mb-2">
              {orderByTable ? (
                <Table className="w-12 h-12 mx-auto mb-2 opacity-50" />
              ) : (
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {orderByTable ? 'No table data available' : 'No customer data available'}
            </p>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data}>
            <XAxis 
              dataKey="label" 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              domain={[0, yAxisMax]}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px'
              }}
              labelStyle={{ color: '#111827', fontWeight: 600 }}
              formatter={(value: number) => [`${value} orders`, orderByTable ? 'Table' : 'Customer']}
            />
            <Bar 
              dataKey="value" 
              fill="#6366f1" 
              radius={[4, 4, 0, 0]}
              maxBarSize={30}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}